<template>
  <div class="bullet-textarea-wrap" :class="{ 'bullet-textarea-wrap--resizable': resizable }">
    <div
      v-if="usePreview"
      class="bullet-textarea-preview"
      aria-hidden="true"
    >
      <template v-for="(seg, i) in segments" :key="i">
        <div v-if="seg.type === 'plain'" class="bullet-text-plain">{{ seg.text }}</div>
        <div v-else-if="seg.type === 'numbered'" class="bullet-text-item">
          <span class="bullet-text-marker">{{ seg.number }}.</span>
          <span class="bullet-text-content">{{ seg.text }}</span>
        </div>
        <div v-else class="bullet-text-item">
          <span class="bullet-text-marker">•</span>
          <span class="bullet-text-content">{{ seg.text }}</span>
        </div>
      </template>
    </div>
    <textarea
      ref="input"
      :value="modelValue"
      :rows="rows"
      :placeholder="placeholder"
      class="form-control form-control-sm bullet-textarea-input"
      :class="{
        'bullet-textarea-input--preview': usePreview,
        'bullet-textarea-input--resizable': resizable,
      }"
      :style="mergedInputStyle"
      @input="onInput"
      @scroll="syncScroll"
      @keydown="onKeydown"
      @keyup="syncMention"
      @click="syncMention"
      @blur="closeMentionSoon"
    ></textarea>
    <MentionSuggest
      v-if="mentions"
      :open="mentionOpen"
      :items="mentionFiltered"
      :active-index="mentionIndex"
      :anchor="mentionAnchor"
      @pick="insertMentionUser"
      @hover="mentionIndex = $event"
    />
  </div>
</template>

<script>
import { parseMixedContent, segmentIsListItem } from "@/utils/bulletText";
import MentionSuggest from "@/components/MentionSuggest.vue";
import {
  filterMentionUsers,
  getMentionQuery,
  insertMention,
  loadMentionUsers,
} from "@/utils/mentionUsers";

export default {
  name: "BulletTextarea",
  components: { MentionSuggest },
  props: {
    modelValue: { type: String, default: "" },
    rows: { type: [Number, String], default: 3 },
    placeholder: { type: String, default: "" },
    inputStyle: { type: Object, default: () => ({}) },
    resizable: { type: Boolean, default: false },
    mentions: { type: Boolean, default: false },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      mentionUsers: [],
      mentionOpen: false,
      mentionQuery: null,
      mentionIndex: 0,
      mentionAnchor: null,
    };
  },
  computed: {
    segments() {
      return parseMixedContent(this.modelValue);
    },
    usePreview() {
      return this.segments?.some(segmentIsListItem) ?? false;
    },
    mergedInputStyle() {
      const style = { ...this.inputStyle };
      if (this.resizable) {
        style.resize = "vertical";
        style.minHeight = style.minHeight || "4.5rem";
        style.overflow = "auto";
      }
      return style;
    },
    mentionFiltered() {
      return filterMentionUsers(this.mentionUsers, this.mentionQuery?.query);
    },
  },
  watch: {
    modelValue() {
      this.$nextTick(this.syncScroll);
    },
    resizable() {
      this.$nextTick(() => this.setupResizeObserver());
    },
    mentionFiltered() {
      this.mentionIndex = 0;
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.syncScroll();
      this.setupResizeObserver();
    });
    if (this.mentions) {
      loadMentionUsers().then((rows) => {
        this.mentionUsers = rows;
      });
    }
  },
  beforeUnmount() {
    this.teardownResizeObserver();
  },
  methods: {
    onInput(e) {
      this.$emit("update:modelValue", e.target.value);
      this.$nextTick(() => {
        this.syncScroll();
        this.syncMention();
      });
    },
    onKeydown(e) {
      if (!this.mentions || !this.mentionOpen || !this.mentionFiltered.length) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        this.mentionIndex = (this.mentionIndex + 1) % this.mentionFiltered.length;
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        this.mentionIndex = (this.mentionIndex - 1 + this.mentionFiltered.length) % this.mentionFiltered.length;
      } else if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        this.insertMentionUser(this.mentionFiltered[this.mentionIndex]);
      } else if (e.key === "Escape") {
        e.preventDefault();
        this.mentionOpen = false;
      }
    },
    syncMention() {
      if (!this.mentions) return;
      const el = this.$refs.input;
      if (!el) return;
      const found = getMentionQuery(el.value, el.selectionStart);
      this.mentionQuery = found;
      this.mentionOpen = !!found;
      if (found) {
        const rect = el.getBoundingClientRect();
        this.mentionAnchor = {
          top: rect.bottom + 4,
          left: rect.left,
          width: Math.max(220, Math.min(rect.width, 320)),
        };
      }
    },
    insertMentionUser(user) {
      const el = this.$refs.input;
      if (!el || !user || !this.mentionQuery) return;
      const next = insertMention(el.value, el.selectionStart, this.mentionQuery.start, user);
      this.$emit("update:modelValue", next.value);
      this.mentionOpen = false;
      this.mentionQuery = null;
      this.$nextTick(() => {
        el.focus();
        el.setSelectionRange(next.cursor, next.cursor);
        this.syncScroll();
      });
    },
    closeMentionSoon() {
      window.setTimeout(() => {
        this.mentionOpen = false;
      }, 120);
    },
    syncScroll() {
      const el = this.$refs.input;
      const preview = this.$el?.querySelector(".bullet-textarea-preview");
      if (!el || !preview) return;
      preview.style.height = `${el.offsetHeight}px`;
      preview.scrollTop = el.scrollTop;
    },
    setupResizeObserver() {
      this.teardownResizeObserver();
      const el = this.$refs.input;
      if (!this.resizable || !el || typeof ResizeObserver === "undefined") return;
      this._resizeObs = new ResizeObserver(() => {
        this.syncScroll();
      });
      this._resizeObs.observe(el);
    },
    teardownResizeObserver() {
      if (this._resizeObs) {
        this._resizeObs.disconnect();
        this._resizeObs = null;
      }
    },
  },
};
</script>

<style scoped>
.bullet-textarea-wrap {
  position: relative;
  flex: 1;
  min-width: 0;
}

.bullet-textarea-preview {
  position: absolute;
  inset: 0;
  z-index: 1;
  margin: 0;
  padding: 0.35rem 0.55rem;
  overflow: hidden;
  pointer-events: none;
  font-size: 0.78rem;
  line-height: 1.45;
}

.bullet-textarea-input {
  position: relative;
  z-index: 2;
  width: 100%;
  resize: none;
  font-size: 0.78rem;
  line-height: 1.45;
  background: transparent;
}

.bullet-textarea-input--resizable {
  resize: vertical !important;
  overflow: auto !important;
}

.bullet-textarea-wrap--resizable .bullet-textarea-preview {
  bottom: auto;
  height: 100%;
}

.bullet-textarea-input--preview {
  color: transparent;
  caret-color: #344767;
}

.bullet-text-plain {
  margin-bottom: 0.2rem;
  word-break: break-word;
  white-space: pre-wrap;
}

.bullet-text-plain:last-child {
  margin-bottom: 0;
}

.bullet-text-item {
  display: flex;
  align-items: flex-start;
  gap: 0.4rem;
  margin-bottom: 0.2rem;
}

.bullet-text-item:last-child {
  margin-bottom: 0;
}

.bullet-text-marker {
  flex-shrink: 0;
  color: #67748e;
  font-weight: 700;
}

.bullet-text-content {
  flex: 1;
  min-width: 0;
  word-break: break-word;
  white-space: pre-wrap;
}
</style>
